import type {
  QuickStudyResultsView,
  QuickStudyAuthoredInstrument,
  QuickStudyDraftView,
  QuickStudyRunView,
  QuickStudyQuote,
  QuickStudyFindingsPostResult,
  QuickStudyExploreData,
} from "@digital-twins/shared-types";
import httpClient from "./http-client";

export interface QuickStudyAudienceEdit {
  plannedSampleSize: number;
  populationFilter?: Record<string, unknown> | null;
  segmentLabel?: string | null;
}
function root(thread: string) {
  return `/knowledge/threads/${encodeURIComponent(thread)}/quick-studies`;
}
function draftPath(thread: string, draft: string) {
  return `${root(thread)}/${encodeURIComponent(draft)}`;
}
function runPath(thread: string, draft: string, run: string) {
  return `${draftPath(thread, draft)}/runs/${encodeURIComponent(run)}`;
}
export const quickStudiesApi = {
  async list(thread: string) {
    return (
      await httpClient.get<{ items: QuickStudyDraftView[] }>(root(thread))
    ).data.items;
  },
  async read(
    thread: string,
    draft: string,
    runToken?: string,
    revision?: number,
  ) {
    return (
      await httpClient.get<QuickStudyDraftView>(draftPath(thread, draft), {
        params: {
          ...(runToken ? { runToken } : {}),
          ...(revision !== undefined ? { revision } : {}),
        },
      })
    ).data;
  },
  async quote(thread: string, draft: string, expectedRevision: number) {
    return (
      await httpClient.post<QuickStudyQuote>(
        `${draftPath(thread, draft)}/quote`,
        { expectedRevision },
      )
    ).data;
  },
  async save(
    thread: string,
    draft: string,
    expectedRevision: number,
    operationId: string,
    instrument: QuickStudyAuthoredInstrument,
  ) {
    return (
      await httpClient.post<QuickStudyDraftView>(
        `${draftPath(thread, draft)}/save`,
        { expectedRevision, operationId, instrument },
      )
    ).data;
  },
  /**
   * The card's respondents and filter edit: a new revision with the same
   * questions. The audience token stays the revision's own.
   */
  async saveAudience(
    thread: string,
    draft: string,
    expectedRevision: number,
    audience: QuickStudyAudienceEdit,
  ) {
    return (
      await httpClient.post<QuickStudyDraftView>(
        `${draftPath(thread, draft)}/audience`,
        { expectedRevision, operationId: crypto.randomUUID(), audience },
      )
    ).data;
  },
  async accept(thread: string, draft: string, quote: QuickStudyQuote) {
    return (
      await httpClient.post<QuickStudyRunView>(
        `${draftPath(thread, draft)}/accept`,
        { quoteToken: quote.quoteToken, expectedRevision: quote.revision },
      )
    ).data;
  },
  async cancelDraft(thread: string, draft: string) {
    return (
      await httpClient.post<
        | { kind: "cancelled"; draftToken: string }
        | { kind: "run_cancellation_required"; runToken: string }
      >(`${draftPath(thread, draft)}/cancel`)
    ).data;
  },
  async cancelRun(thread: string, draft: string, run: string) {
    return (
      await httpClient.post<QuickStudyRunView>(
        `${runPath(thread, draft, run)}/cancel`,
      )
    ).data;
  },
  async findings(thread: string, draft: string, run: string) {
    return (
      await httpClient.get<QuickStudyResultsView>(
        `${runPath(thread, draft, run)}/findings`,
      )
    ).data;
  },
  /**
   * A completed run's frozen survey, rows and audience columns: the report
   * document reads its question helper text and segment options from it.
   */
  async runData(thread: string, draft: string, run: string) {
    return (
      await httpClient.get<QuickStudyExploreData>(
        `${runPath(thread, draft, run)}/data`,
      )
    ).data;
  },
  async postFindings(thread: string, draft: string, run: string) {
    return (
      await httpClient.post<QuickStudyFindingsPostResult>(
        `${runPath(thread, draft, run)}/findings/post`,
      )
    ).data;
  },
};
